import { Popover } from '@execonline-inc/execonline-ui';
import { nothing } from 'maybeasy';
import * as React from 'react';
import { T } from '../../../../../../Translations';
import Checkmark from '../../../../../Cutesies/Checkmark';
import CopyIcon from '../../../../../Cutesies/CopyIcon';
import Loader from '../../../../../Loader';
import AdvancedSettings from './AdvancedSettings';

interface Props {
  href: string;
  isLoading: boolean;
}

const CopyableUrl: React.FC<Props> = ({ href, isLoading }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [copyState, setCopyState] = React.useState<'waiting' | 'copying' | 'copied'>('waiting');

  // Reset copy state after showing "copied" feedback, with cleanup to prevent memory leak
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (copyState === 'copied') {
      timeoutId = setTimeout(() => setCopyState('waiting'), 2000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [copyState]);

  const handleCopyUrl = async () => {
    setCopyState('copying');
    try {
      await navigator.clipboard.writeText(href);
      setCopyState('copied');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback: Select text for manual copy
      if (inputRef.current) {
        inputRef.current.select();
        try {
          document.execCommand('copy');
          setCopyState('copied');
        } catch (fallbackErr) {
          console.error('Fallback copy also failed:', fallbackErr);
          setCopyState('waiting');
        }
      } else {
        setCopyState('waiting');
      }
    }
  };

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={href}
          disabled={isLoading}
          className={`w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm mb-3 bg-white ${
            isLoading ? 'text-gray-400' : 'text-gray-900'
          }`}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 -mt-1.5">
            <Loader
              type="spin"
              text={nothing()}
              height={20}
              width={20}
              color="#9CA3AF"
              className="h-full"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-4 mb-3">
        <button
          type="button"
          className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 border rounded-md font-medium text-sm transition-colors whitespace-nowrap h-[42px] ${
            isLoading
              ? 'border-gray-300 text-gray-400 cursor-not-allowed'
              : 'border-teal-600 text-teal-600 hover:bg-teal-50'
          }`}
          onClick={handleCopyUrl}
          data-intercom-target="copy-url-button"
          disabled={isLoading || copyState === 'copying'}
        >
          <span className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center relative pointer-events-none">
              <span
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
                  copyState === 'waiting' ? 'opacity-100' : 'opacity-0'
                } ${isLoading ? 'opacity-50' : ''}`}
              >
                <CopyIcon />
              </span>
              <span
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
                  copyState === 'copying' ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {/*
                 * h-full: Loader defaults to h-screen, which, though hidden,
                 * stretched the Share dialog's scrolling body by a window's
                 * height (DVLP-8217).
                 */}
                <Loader
                  type="spin"
                  text={nothing()}
                  height={24}
                  width={24}
                  color="#187A90"
                  className="h-full"
                />
              </span>
              <span
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
                  copyState === 'copied' ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <Checkmark className="fill-teal-600" />
              </span>
            </span>
            <T kind="Copy URL" />
          </span>
        </button>
        {/*
         * DVLP-7559 — Advanced Settings lives in a click-triggered popover
         * anchored to this button (Google Drive share-dialog pattern). This
         * keeps the main share modal at a constant compact size regardless
         * of whether the user is engaging with the extra unlicensed-access
         * options.
         */}
        <Popover
          trigger="click"
          placement="bottom"
          className="w-[440px] p-4 bg-card rounded-md shadow-lg border border-border text-foreground"
          renderTrigger={(ref, getReferenceProps) => (
            <button
              ref={ref}
              type="button"
              className="ml-6 text-teal-600 font-semibold text-sm hover:text-teal-700"
              data-intercom-target="share-advanced-settings-button"
              {...getReferenceProps()}
            >
              <T kind="Advanced Settings" />
            </button>
          )}
        >
          <AdvancedSettings />
        </Popover>
      </div>
    </div>
  );
};

export default CopyableUrl;
